import { pick } from 'lodash-es';
import type { NextBlock } from 'src/redux/types';
import { useObservableBlock } from 'src/services/rxjs-redux/hook';

// 默认一定会返回的字段，减少使用者填写的字段数量
type RequireField = 'uuid' | 'spaceId' | 'parentId' | 'type';

type PartialBlock<Field extends keyof Omit<NextBlock, RequireField>, DataField> = {
  [K in keyof Pick<NextBlock, RequireField>]: NextBlock[K]; // 获取RequireField相关通用字段
} & {
  [K in keyof Pick<NextBlock, Field> as K extends 'data' ? never : K]: NextBlock[K]; // 获取非data字段
} & {
  // 下面是data数据的获取,当dataProps有传递数据的时候，data只返回dataProps的数据
  [K in keyof Pick<NextBlock, Field> as K extends 'data' ? K : never]: {
    [K in keyof NextBlock['data'] as K extends DataField ? K : never]: NextBlock['data'][K];
  };
};

/**
 * 为了解决useBlock导致的组件重复渲染的问题而写的hook，只pick自己关心的数据，返回一个新的对象
 * 默认会把一些基本不变更的字段(见RequireField)返回，减少使用负担.
 * pickBlock在useSelector里面，需要配合deepEqual使用
 */
export function pickBlock<
  Field extends keyof Omit<NextBlock, RequireField>, // 可以输入除了RequireField之外的其它类型
  DataField extends keyof NextBlock['data']
>(
  block: NextBlock,
  blockProps: Field[],
  dataProps?: DataField[]
  // 返回只有U和RequireField字段的类型
): PartialBlock<Field, DataField> {
  const partialBlock = pick(block, ['uuid', 'spaceId', 'parentId', 'type', ...blockProps]);
  if (dataProps) {
    const partialData = pick(block.data, dataProps);
    // @ts-ignore compile error
    return { ...partialBlock, data: partialData };
  }
  // @ts-ignore compile error
  return partialBlock;
}

export function usePickBlock<
  Field extends keyof Omit<NextBlock, RequireField>, // 可以输入除了RequireField之外的其它类型
  DataField extends keyof NextBlock['data']
>(
  uuid: string | undefined,
  blockFields: Field[],
  dataFields?: DataField[],
  opt?: {
    useRedux?: boolean;
    wait?: number;
  }
) {
  return useObservableBlock(
    uuid,
    (block) => {
      if (block) {
        return pickBlock(block, blockFields, dataFields);
      }
    },
    [],
    { useRedux: opt?.useRedux, wait: opt?.wait }
  );
}
